/**
 * AttachmentPreviewDialog — in-app preview of an email attachment without
 * downloading it. Fetches a short-lived `inline`-disposition presigned URL and
 * renders images / PDFs in-page; other types fall back to a download prompt.
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Download, FolderInput, ExternalLink } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';

export interface PreviewAttachment {
  attachmentId: string;
  filename: string;
  mimeType: string;
  /** Underlying Vault document id; present ⇒ the attachment can be saved to a folder. */
  documentId?: string | null;
}

const isImage = (m: string) => m.startsWith('image/');
// Senders / IMAP parsers label PDFs inconsistently (application/pdf, x-pdf,
// acrobat, or a generic octet-stream with a .pdf name). Detect by either, so the
// preview affordance reliably appears and the iframe gets the PDF branch.
const PDF_MIME = /^application\/(pdf|x-pdf|acrobat|vnd\.pdf)$/i;
const hasPdfExt = (name?: string) => !!name && /\.pdf$/i.test(name.trim());
const isPdf = (m: string, name?: string) => PDF_MIME.test(m) || hasPdfExt(name);
const isText = (m: string) =>
  m.startsWith('text/') || m === 'application/json' || m === 'application/xml';

/** Whether the in-app preview can render this attachment (image / PDF / text). */
export function isPreviewableAttachment(mimeType: string, filename?: string): boolean {
  return isImage(mimeType) || isPdf(mimeType, filename) || isText(mimeType);
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  messageId: string | null;
  attachment: PreviewAttachment | null;
  /** Save a copy to a Vault folder; shown only when the attachment is saveable. */
  onSaveToVault?: () => void;
}

export function AttachmentPreviewDialog({
  open,
  onOpenChange,
  messageId,
  attachment,
  onSaveToVault,
}: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !messageId || !attachment) {
      setUrl(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setUrl(null);
    api
      .getEmailMailboxAttachmentDownloadUrl(messageId, attachment.attachmentId, 'inline')
      .then((res) => {
        if (!cancelled) setUrl(res.url);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load preview');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, messageId, attachment]);

  const download = async () => {
    if (!messageId || !attachment) return;
    try {
      const { url: dl } = await api.getEmailMailboxAttachmentDownloadUrl(
        messageId,
        attachment.attachmentId,
      );
      const a = document.createElement('a');
      a.href = dl;
      a.download = attachment.filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast({
        title: 'Couldn’t download attachment',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Open the attachment in a new browser tab via its short-lived inline URL —
  // the reliable cross-browser way to "open" a PDF when the in-iframe viewer is
  // unavailable (Firefox, blocked plugin, etc.). Fetches a fresh URL so it works
  // even if the inline preview above failed to load.
  const openInNewTab = async () => {
    if (!messageId || !attachment) return;
    try {
      const { url: inlineUrl } = await api.getEmailMailboxAttachmentDownloadUrl(
        messageId,
        attachment.attachmentId,
        'inline',
      );
      window.open(inlineUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast({
        title: 'Couldn’t open attachment',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const mime = attachment?.mimeType ?? '';
  const pdf = isPdf(mime, attachment?.filename);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="truncate pr-6">
            {attachment?.filename ?? 'Attachment'}
          </DialogTitle>
        </DialogHeader>
        <div className="flex max-h-[70vh] min-h-[50vh] items-center justify-center overflow-auto">
          {loading ? (
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          ) : error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : url && isImage(mime) ? (
            <img
              src={url}
              alt={attachment?.filename ?? ''}
              className="max-h-[68vh] max-w-full object-contain"
            />
          ) : url && pdf ? (
            // Sandboxed as defense-in-depth: the server only ever serves an
            // inline, script-safe content-type here (html/svg are downgraded to a
            // download), and the browser's native PDF viewer still renders under
            // an empty sandbox. Falls back to the Download / Open-in-tab actions.
            <iframe
              src={url}
              title={attachment?.filename ?? 'PDF preview'}
              sandbox=""
              className="h-[68vh] w-full"
            />
          ) : url && isText(mime) ? (
            // Sandboxed so a text/html attachment can't run scripts on preview.
            <iframe
              src={url}
              title={attachment?.filename ?? 'Text preview'}
              sandbox=""
              className="h-[68vh] w-full bg-white"
            />
          ) : (
            <div className="text-center text-sm text-muted-foreground">
              <p>Preview isn’t available for this file type.</p>
              <p className="text-xs">{mime || 'unknown type'}</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button variant="outline" className="gap-1" onClick={openInNewTab}>
            <ExternalLink className="h-4 w-4" /> Open in new tab
          </Button>
          {onSaveToVault && attachment?.documentId && (
            <Button variant="outline" className="gap-1" onClick={onSaveToVault}>
              <FolderInput className="h-4 w-4" /> Save to Vault
            </Button>
          )}
          <Button className="gap-1" onClick={download}>
            <Download className="h-4 w-4" /> Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
